import { Box, Button, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { GoBack } from '../../../../components/goBack'
import StepperCreate from '../../../../components/stepperCreate'
import SimpleModal from '../../../../components/modal/simpleModal'
import SnackSimple from '../../../../components/snackbars/simple'
import { actionPlansServices } from '@/service/actionPlans'
import { sharedAws } from '@/service/bucketS3/Storage'
import { httpFetch } from '@/utils/crud'
import { Item } from '@/utils/types/generals'
import { isEmail } from '@/utils/validate/validate'
import { ManualActionPlanData, ManualActionPlanErrors, PlanIncludeOption } from './types'
import { useStyles } from './styles'
import Step1BasicInfo from './fragments/step1BasicInfo'
import Step2SelectUsers from './fragments/step2SelectUsers'
import Step3Notifications from './fragments/step3Notifications'
import { buildManualActionPlanPayload } from './methods'
import { useContextState } from '@/hooks/theme'
import { Types } from '@/utils/enums'
import { Roles } from '@/utils/enums'
import { getLocalStorage } from '@/utils/crud/crypt'
import { DEFAULT_PLAN_EVIDENCES, DEFAULT_PLAN_ITEMS } from './constants'
import { useActionPlanUsers, AssignedUserFilters, ApproverFilters } from './hooks/useActionPlanUsers'
import { getInternalUsers } from '../createActionPlan/methods'
import { AvailableUser, InternalUser } from '../createActionPlan/types'
import { listRoles } from '@/service/roles'

const INITIAL_DATA: ManualActionPlanData = {
  title: '',
  instructions: '',
  endDate: null,
  sendEmailCopy: false,
  includes: [],
  items: DEFAULT_PLAN_ITEMS,
  linkQuestionnaire: { questionnaireId: null, questionIds: [] },
  evidences: DEFAULT_PLAN_EVIDENCES,
  solverEvidences: [],
  assignedUser: null,
  approvers: [],
  selectedInternalUsers: [],
  externalEmails: [],
}

const CreateActionPlanManual = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const styles = useStyles()
  const { colors, state: globalState } = useContextState()

  const [step, setStep] = useState(0)
  const [data, setData] = useState<ManualActionPlanData>(INITIAL_DATA)
  const [errors, setErrors] = useState<ManualActionPlanErrors>({})
  const [activeConfig, setActiveConfig] = useState<PlanIncludeOption | null>(null)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'error' as Types,
  })

  const [appliedAssignedFilters, setAppliedAssignedFilters] = useState<Record<string, Item[]>>({})
  const [appliedApproverFilters, setAppliedApproverFilters] = useState<Record<string, Item[]>>({})

  const [internalUserOptions, setInternalUserOptions] = useState<InternalUser[]>([])
  const [isLoadingInternalUsers, setIsLoadingInternalUsers] = useState(false)
  const [emailsRawInput, setEmailsRawInput] = useState(data.externalEmails.join(', '))

  const loggedUser = useMemo(() => getLocalStorage() as {
    id?: number | string
    role?: Roles
    fullName?: string
    firstName?: string
    lastName?: string
    email?: string
    picture?: string | null
    userType?: { id?: number; name?: string; description?: string }
  }, [])

  const globalUserType = globalState?.userType

  const isCollaborator = loggedUser?.role === Roles.PARTNER

  const loggedAvailableUser: AvailableUser = useMemo(
    () => ({
      id: Number(loggedUser?.id ?? 0),
      firstName: loggedUser?.firstName ?? '',
      lastName: loggedUser?.lastName ?? '',
      fullName: loggedUser?.fullName ?? '',
      roleId: loggedUser?.role ?? 0,
      role: listRoles.find((role) => role.id === loggedUser?.role)?.description ?? '',
      userTypeId: globalUserType?.id ?? loggedUser?.userType?.id ?? 0,
      userType:
        globalUserType?.description
        || globalUserType?.name
        || loggedUser?.userType?.description
        || loggedUser?.userType?.name
        || '',
      email: loggedUser?.email ?? '',
      picture: loggedUser?.picture ?? null,
    }),
    [loggedUser, globalUserType]
  )

  const {
    assignedUsers,
    approverUsers,
    isLoadingAssigned,
    isLoadingApprovers,
    assignedError,
    approversError,
    filterOptions,
    isLoadingFilters,
    applyAssignedFilters,
    applyApproverFilters,
  } = useActionPlanUsers()

  const handleApplyAssignedFilters = useCallback((filters: Record<string, Item[]>, apiFilters: AssignedUserFilters) => {
    setAppliedAssignedFilters(filters)
    applyAssignedFilters(apiFilters)
  }, [applyAssignedFilters])

  const handleApplyApproverFilters = useCallback((filters: Record<string, Item[]>, apiFilters: ApproverFilters) => {
    setAppliedApproverFilters(filters)
    applyApproverFilters(apiFilters)
  }, [applyApproverFilters])

  useEffect(() => {
    setIsLoadingInternalUsers(true)
    getInternalUsers()
      .then(setInternalUserOptions)
      .finally(() => setIsLoadingInternalUsers(false))
  }, [])

  const steps = useMemo(
    () =>
      t('actionPlan.manualCreation.steps', { returnObjects: true }) as Array<{
        step: string
        label: string
      }>,
    [t]
  )

  const handleChange = useCallback((newData: Partial<ManualActionPlanData>) => {
    setData((prev) => ({ ...prev, ...newData }))

    setErrors((prev) => ({
      ...prev,
      ...(newData.title !== undefined && { title: undefined }),
      ...(newData.includes !== undefined && { includes: undefined }),
      ...(newData.endDate !== undefined && { endDate: undefined }),
      ...(newData.assignedUser !== undefined && { assignedUser: undefined }),
      ...(newData.approvers !== undefined && { approvers: undefined }),
      ...(newData.externalEmails !== undefined && { externalEmails: undefined }),
    }))
  }, [])

  useEffect(() => {
    if (isCollaborator && !data.assignedUser) {
      handleChange({ assignedUser: loggedAvailableUser })
    }
  }, [isCollaborator, data.assignedUser, loggedAvailableUser, handleChange])

  const handleClearOption = useCallback((optionId: PlanIncludeOption) => {
    if (optionId === 'items') {
      handleChange({
        items: DEFAULT_PLAN_ITEMS,
        includes: data.includes.filter((id) => id !== 'items'),
      })
    }

    if (optionId === 'linkQuestionnaire') {
      handleChange({
        linkQuestionnaire: { questionnaireId: null, questionIds: [] },
        includes: data.includes.filter((id) => id !== 'linkQuestionnaire'),
      })
    }

    if (optionId === 'evidences') {
      const evidenceKeysToDelete = data.evidences.flatMap((evidence) =>
        evidence.files.map((file) => file.key)
      )
      if (evidenceKeysToDelete.length > 0) {
        Promise.all(
          evidenceKeysToDelete.map((key) => sharedAws.delete({ name: key }))
        ).catch((error) => {
          console.error('Error deleting evidence files:', error)
        })
      }

      handleChange({
        evidences: DEFAULT_PLAN_EVIDENCES,
        includes: data.includes.filter((id) => id !== 'evidences'),
      })
    }

    if (optionId === 'solverEvidences') {
      handleChange({
        solverEvidences: [],
        includes: data.includes.filter((id) => id !== 'solverEvidences'),
      })
    }

    if (data.includes.length <= 1) {
      setErrors((prev) => ({
        ...prev,
        includes: t('actionPlan.validation.selectAtLeastOneOption'),
      }))
    }
  }, [data.includes, data.evidences, handleChange, t])

  const validateStep = useCallback((): boolean => {
    const newErrors: ManualActionPlanErrors = {}
    let isValid = true

    if (step === 0) {
      if (!data.title.trim()) {
        newErrors.title = t('general.validations.required')
        isValid = false
      }

      if (data.includes.length === 0) {
        newErrors.includes = t('actionPlan.validation.selectAtLeastOneOption')
        isValid = false
      }

      if (!data.endDate) {
        newErrors.endDate = t('general.validations.required')
        isValid = false
      }
    }

    if (step === 1) {
      if (!data.assignedUser) {
        newErrors.assignedUser = t('actionPlan.validation.selectAtLeastOneUser')
        isValid = false
      }
    }

    if (step === 2) {
      const hasInvalidEmail = data.externalEmails.some((email) => !isEmail(email))
      if (hasInvalidEmail) {
        newErrors.externalEmails = t('actionPlan.validation.invalidEmails')
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }, [data, step, t])

  const isContinueDisabled = useMemo(() => {
    if (step === 0) {
      return !data.title.trim() || !data.endDate || data.includes.length === 0
    }

    if (step === 1) {
      return !data.assignedUser
    }

    if (step === 2) {
      return data.externalEmails.some((email) => !isEmail(email))
    }

    return false
  }, [data, step])

  const handleContinue = useCallback(() => {
    if (!validateStep()) return

    if (step === 2) {
      setIsConfirmModalOpen(true)
      return
    }

    setStep((prev) => Math.min(prev + 1, 2))
  }, [validateStep, step])

  const handleBack = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleCancel = useCallback(() => {
    navigate('/dashboard/action-plan/created')
  }, [navigate])

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false)
    setSaveError('')
  }, [])

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true)
    setSaveError('')

    try {
      const payload = buildManualActionPlanPayload(data, t)
      const response = await actionPlansServices(httpFetch).saveManualActionPlan(payload)

      if (response?.errors) {
        throw new Error(response.errors[0]?.message || t('actionPlan.errorCreatingPlan'))
      }

      navigate('/dashboard/action-plan/created', {
        state: {
          actionPlanCreated: true,
          actionPlanOfStores: false,
        },
      })
    } catch (error) {
      console.error('Error saving manual action plan:', error)
      const message = t('actionPlan.errorCreatingPlan')
      setSaveError(message)
      setIsConfirmModalOpen(false)
      setSnackbar({
        open: true,
        message,
        type: 'error' as Types,
      })
    } finally {
      setIsSaving(false)
    }
  }, [data, navigate, t])

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Step1BasicInfo
            data={data}
            errors={errors}
            onChange={handleChange}
            onClearOption={handleClearOption}
            activeConfig={activeConfig}
            onEnterConfig={setActiveConfig}
            onExitConfig={() => setActiveConfig(null)}
          />
        )
      case 1:
        return (
          <Step2SelectUsers
            data={data}
            errors={errors}
            onChange={handleChange}
            isCollaborator={isCollaborator}
            users={{
              assigned: isCollaborator ? [loggedAvailableUser] : assignedUsers,
              approvers: approverUsers,
              isLoadingAssigned,
              isLoadingApprovers,
              assignedError,
              approversError,
            }}
            filters={{
              options: filterOptions,
              isLoading: isLoadingFilters,
              appliedAssigned: appliedAssignedFilters,
              appliedApprovers: appliedApproverFilters,
              onApplyAssigned: handleApplyAssignedFilters,
              onApplyApprovers: handleApplyApproverFilters,
            }}
          />
        )
      case 2:
        return (
          <Step3Notifications
            data={data}
            errors={errors}
            onChange={handleChange}
            internalUserOptions={internalUserOptions}
            isLoadingInternalUsers={isLoadingInternalUsers}
            emailsRawInput={emailsRawInput}
            setEmailsRawInput={setEmailsRawInput}
          />
        )
      default:
        return null
    }
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box sx={{ '&& .MuiTypography-root': { color: colors.color4 } }}>
            <GoBack path='/dashboard/action-plan/created' title={t('actionPlan.manualCreation.returnToList')}  />
          </Box>
          <Typography variant='title' sx={styles.title}>
            {t('actionPlan.manualCreation.title')}
          </Typography>
        </Box>
        <Box sx={styles.stepperContainer}>
          <StepperCreate steps={steps} activeStep={step} />
        </Box>
      </Box>

      <Box sx={styles.stepsContent}>
        {renderStep()}
      </Box>

      {activeConfig === null && (
        <Box sx={styles.footer}>
          {step > 0 ? (
            <Button variant='text' onClick={handleBack}>
              {t('general.return')}
            </Button>
          ) : (
            <Button variant='text' onClick={handleCancel}>
              {t('general.cancel')}
            </Button>
          )}
          <Button
            variant='contained'
            color='primary'
            onClick={handleContinue}
            disabled={isContinueDisabled}
            sx={styles.continueButton}
          >
            {t('general.continue')}
          </Button>
        </Box>
      )}

      <SimpleModal
        open={isConfirmModalOpen}
        close={handleCloseConfirmModal}
        back={handleCloseConfirmModal}
        next={handleConfirmSave}
        title={t('actionPlan.manualCreation.confirmSave.title')}
        description={t('actionPlan.manualCreation.confirmSave.description')}
        cancelText={t('general.cancel')}
        buttonText={t('general.confirm')}
        isLoading={isSaving}
        sx={{ width: '400px', borderRadius: '10px' }}
      />

      <SnackSimple
        open={snackbar.open}
        close={handleCloseSnackbar}
        message={snackbar.message}
        type={snackbar.type}
      />
    </Box>
  )
}

export default CreateActionPlanManual
